import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AxiosError, AxiosResponse } from 'axios';

import { CommonResType } from '@/types/commonApiType';

import { api } from '.';

type GetInfoResType = {
	member_address: string;
	member_address_detail: string;
	member_birth_date: string;
	member_birth_month: string;
	member_birth_year: string;
	member_cell: string;
	member_email: string;
	member_en_name: string;
	member_gender: string;
	member_name: string;
	member_type: string;
	member_zipcode: string;
	member_id: string;
};

// 회원 정보 확인
export const useGetMemberInfoApi = () => {
	const url = '/v1/front/getMemberInfo';
	return useQuery<AxiosResponse, AxiosError, GetInfoResType>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => res.data.data.member_info,
		placeholderData: keepPreviousData,
	});
};

// 회원 정보 수정
type ModifyMemberInfoDataType = {
	member_name: string;
	member_en_name: string;
	member_birth_year: string;
	member_birth_month: string;
	member_birth_date: string;
	member_gender: string;
	member_zipcode: string;
	member_address: string;
	member_address_detail: string;
	member_cell: string;
	member_email: string;
};

export const usePostModifyMemberInfoApi = () => {
	const url = '/v1/front/modifyMemberInfo';
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse<CommonResType>, AxiosError, ModifyMemberInfoDataType>({
		mutationFn: (data: ModifyMemberInfoDataType) => api.post(url, data),
		onSuccess: (res) => {
			if (res.data.success) {
				queryClient.invalidateQueries({ queryKey: ['/v1/front/getMemberInfo'] });
			}
		},
	});
};

type GetMemberCompanyInfoResType = {
	member_company_address: string;
	member_company_address_detail: string;
	member_company_en_name: string;
	member_company_fax: string;
	member_company_name: string;
	member_company_tel: string;
	member_position: string;
	member_company_zipcode: string;
};

// 직장 정보 확인
export const useGetMemberCompanyInfoApi = () => {
	const url = '/v1/front/getMemberCompanyInfo';
	return useQuery<AxiosResponse, AxiosError, GetMemberCompanyInfoResType>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => res.data.data.member_company_info,
		placeholderData: keepPreviousData,
	});
};

// 직장 정보 수정
type ModifyMemberCompanyInfoDataType = {
	member_company_name: string;
	member_company_en_name: string;
	member_position: string;
	member_company_tel: string;
	member_company_zipcode: string;
	member_company_address: string;
	member_company_address_detail: string;
};

export const usePostModifyMemberCompanyInfoApi = () => {
	const url = '/v1/front/modifyMemberCompanyInfo';
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse<CommonResType>, AxiosError, ModifyMemberCompanyInfoDataType>({
		mutationFn: (data: ModifyMemberCompanyInfoDataType) => api.post(url, data),
		onSuccess: (res) => {
			if (res.data.success) {
				queryClient.invalidateQueries({ queryKey: ['/v1/front/getMemberCompanyInfo'] });
			}
		},
	});
};

type GetMemberEduInfoResType = {
	member_edu_school: string;
	member_edu_specialty: string;
	member_edu_degree: string;
	member_edu_gradation_year: string;
	member_edu_nation: string;
};

// 직장 정보 확인
export const useGetMemberEduInfoApi = () => {
	const url = '/v1/front/getMemberEduInfo';
	return useQuery<AxiosResponse, AxiosError, GetMemberEduInfoResType[]>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => res.data.data.member_edu_info,
		placeholderData: keepPreviousData,
	});
};

// 직장 정보 수정
type ModifyMemberEduInfoDataType = {
	member_edu_school: string;
	member_edu_specialty: string;
	member_edu_degree: string;
	member_edu_gradation_year: string;
	member_edu_nation: string;
};

export const usePostModifyMemberEduInfoApi = () => {
	const url = '/v1/front/modifyMemberEduInfo';
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse<CommonResType>, AxiosError, ModifyMemberEduInfoDataType[]>({
		mutationFn: (data: ModifyMemberEduInfoDataType[]) => api.post(url, data),
		onSuccess: (res) => {
			if (res.data.success) {
				queryClient.invalidateQueries({ queryKey: ['/v1/front/getMemberEduInfo'] });
			}
		},
	});
};

type GetMemberEtcInfoResType = {
	member_research_topic: string;
	member_recommender: string;
	member_receive_post: string;
};

// 기타 정보 확인
export const useGetMemberEtcInfoApi = () => {
	const url = '/v1/front/getMemberEtcInfo';
	return useQuery<AxiosResponse, AxiosError, GetMemberEtcInfoResType>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => res.data.data.member_etc_info,
		placeholderData: keepPreviousData,
	});
};

// 기타 정보 수정
type ModifyMemberEtcInfoDataType = {
	member_research_topic: string;
	member_receive_post: string;
};

export const usePostModifyMemberEtcInfoApi = () => {
	const url = '/v1/front/modifyMemberEtcInfo';
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse<CommonResType>, AxiosError, ModifyMemberEtcInfoDataType>({
		mutationFn: (data: ModifyMemberEtcInfoDataType) => api.post(url, data),
		onSuccess: (res) => {
			if (res.data.success) {
				queryClient.invalidateQueries({ queryKey: ['/v1/front/getMemberEtcInfo'] });
			}
		},
	});
};

// 재학증명서 등록
export const useEnrollStudentCertificateApi = () => {
	const url = '/v1/front/enrollStudentCertificate';
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse<CommonResType>, AxiosError, FormData>({
		mutationFn: (data: FormData) => api.post(url, data),
		onSuccess: (res) => {
			if (res.data.success) {
				// queryClient.invalidateQueries({ queryKey: ['/v1/front/getMemberEtcInfo'] });
			}
		},
	});
};

// 재학 증명서 여부 확인
type GetStudentCertificateResType = {
	file_name: string;
	file_path: string;
	file_pk: string;
	file_size: number;
	member_student_certificate_yn: string;
};

export const useGetStudentCertificateApi = () => {
	const url = '/v1/front/getStudentCertificate';
	return useQuery<AxiosResponse, AxiosError, GetStudentCertificateResType>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => res.data.data,
		placeholderData: keepPreviousData,
	});
};
